/**
 * Human summaries for schedule cards and read-only states
 * (docs/plans/planned-task-schedule.md §6.9).
 *
 * Maintenance / cleaning trade language, e.g.
 *   'Every Tue & Fri · never ends'
 *   '2nd Thursday of the month · ends after 12 services'
 *   'Every 2 days · Sep–Mar'
 *   '6 months after each service'
 *   'Every 2nd visit'
 *
 * Deterministic: same editor model in ⇒ identical string out.
 */

import { ScheduleAfterServiceRule, ScheduleCalendarRule, ScheduleDatesRule, ScheduleLeeway, ScheduleRule, ScheduleEnds, ScheduleNth, SchedulePerVisitRule, ScheduleWeekday } from './schedule.rule';
import { isWorkingDaysCalendar, normaliseScheduleDates } from './schedule.compile';

const SEPARATOR = ' · ';

/**
 * The working-day default reads as one phrase with no ends clause and no non-working-day segment:
 * it never ends, and it cannot land on a non-working day, so there is nothing for either clause to
 * say. An absent schedule and the explicit 'workingDays' preset describe identically.
 */
const WORKING_DAYS_DESCRIPTION = 'Every working day';

const WORKING_DAYS_SHORT_DESCRIPTION = 'working days';

function isWorkingDaysSchedule(editor: ScheduleRule | undefined): boolean {

    if (!editor) {
        return true;
    }

    return editor.kind === 'calendar' && isWorkingDaysCalendar(editor.calendar);
}

const DAY_NAMES: readonly string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DAY_FULL_NAMES: readonly string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const MONTH_NAMES: readonly string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ORDINAL_NAMES: readonly string[] = ['1st', '2nd', '3rd', '4th'];

function ordinal(value: number): string {

    if (value >= 1 && value <= 4) {
        return ORDINAL_NAMES[value - 1];
    }

    const suffix = value % 10 === 1 && value % 100 !== 11 ? 'st'
        : value % 10 === 2 && value % 100 !== 12 ? 'nd'
            : value % 10 === 3 && value % 100 !== 13 ? 'rd'
                : 'th';

    return `${value}${suffix}`;
}

function nthLabel(nth: ScheduleNth): string {

    return nth === 'last' ? 'last' : ordinal(nth);
}

/** 'Tue & Fri', 'Mon, Wed & Fri'. */
function dayList(days: readonly ScheduleWeekday[] | undefined): string {

    const names = Array.from(new Set(days ?? [1]))
        .sort((a, b) => a - b)
        .map(day => DAY_NAMES[day - 1]);

    if (names.length === 1) {
        return names[0];
    }

    return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/** 'the 1st', 'the last day', '1st and 15th' — a single entry keeps its article, a list drops it. */
function daysOfMonthLabel(daysOfMonth: readonly (number | 'last')[] | undefined): string {

    const days = sortMonthDays(daysOfMonth);

    if (days.length === 1) {
        return days[0] === 'last' ? 'the last day' : `the ${ordinal(days[0])}`;
    }

    return joinWithAnd(days.map(day => (day === 'last' ? 'last day' : ordinal(day))));
}

/** Ascending, with 'last' pushed to the end. */
function sortMonthDays(daysOfMonth: readonly (number | 'last')[] | undefined): (number | 'last')[] {

    const days = daysOfMonth && daysOfMonth.length > 0 ? daysOfMonth : [1];

    return Array.from(new Set(days)).sort((a, b) => {

        if (a === 'last') {
            return b === 'last' ? 0 : 1;
        }

        return b === 'last' ? -1 : a - b;
    });
}

/** 'a and b' / 'a, b and c'. */
function joinWithAnd(values: readonly string[]): string {

    if (values.length === 1) {
        return values[0];
    }

    return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
}

/** Contiguous month runs render as a season range ('Sep–Mar'), otherwise a list ('Apr & Oct'). */
function monthLabel(months: readonly number[] | undefined): string | undefined {

    if (!months || months.length === 0) {
        return undefined;
    }

    const unique = Array.from(new Set(months)).sort((a, b) => a - b);

    if (unique.length === 1) {
        return MONTH_NAMES[unique[0] - 1];
    }

    const season = seasonRange(unique);

    if (season) {
        return season;
    }

    const names = unique.map(month => MONTH_NAMES[month - 1]);

    return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/** Detect a run of consecutive months, allowing a wrap across the new year (Sep–Mar). */
function seasonRange(months: readonly number[]): string | undefined {

    if (months.length < 3) {
        return undefined;
    }

    const present = new Set(months);

    for (let start = 1; start <= 12; start += 1) {
        if (!present.has(start) || present.has(previousMonth(start))) {
            continue;
        }

        let cursor = start;
        let counted = 1;

        while (counted < months.length && present.has(nextMonth(cursor))) {
            cursor = nextMonth(cursor);
            counted += 1;
        }

        if (counted === months.length) {
            return `${MONTH_NAMES[start - 1]}–${MONTH_NAMES[cursor - 1]}`;
        }
    }

    return undefined;
}

function nextMonth(month: number): number {

    return month === 12 ? 1 : month + 1;
}

function previousMonth(month: number): number {

    return month === 1 ? 12 : month - 1;
}

function endsLabel(ends: ScheduleEnds | undefined): string {

    if (!ends || ends.type === 'never') {
        return 'never ends';
    }

    if (ends.type === 'onDate') {
        return `ends ${ends.date}`;
    }

    return `ends after ${ends.count} ${ends.count === 1 ? 'service' : 'services'}`;
}

function policyLabel(calendar: ScheduleCalendarRule): string | undefined {

    switch (calendar.nonWorkingDayPolicy) {
        case 'skip':
            return 'skips non-working days';
        case 'next':
            return 'moves to the next working day';
        case 'previous':
            return 'moves to the previous working day';
        case 'nearest':
            return 'moves to the nearest working day';
        default:
            return undefined;
    }
}

/** 'the month' / 'the quarter' / 'every 2 months' — the interval half of a monthly-family summary. */
function monthCadence(interval: number): string {

    if (interval === 1) {
        return 'the month';
    }

    if (interval === 3) {
        return 'the quarter';
    }

    return `every ${interval} months`;
}

function dailyPattern(calendar: ScheduleCalendarRule): string {

    switch (calendar.dailyPreset ?? 'everyDay') {
        case 'weekdays':
            return 'Every weekday';
        case 'sixDay':
            return 'Mon to Sat';
        case 'weekend':
            return 'Weekends';
        case 'everyNDays': {
            const interval = calendar.dailyInterval ?? 2;

            return interval === 1 ? 'Every day' : `Every ${interval} days`;
        }
        default:
            return 'Every day';
    }
}

/**
 * Fortnightly reads two ways: A/B is a strict fortnight ('Every second Wed'),
 * while ISO parity is calendar-driven and names the days plainly, leaving the
 * parity to its own summary segment ('Tue & Fri · odd weeks (ISO)').
 */
function fortnightlyPattern(calendar: ScheduleCalendarRule): string {

    if (isIsoParity(calendar)) {
        return dayList(calendar.daysOfWeek);
    }

    return weeklyPattern(calendar, 2);
}

function isIsoParity(calendar: ScheduleCalendarRule): boolean {

    return calendar.repeat === 'fortnightly' && (calendar.weekSelector === 'isoOdd' || calendar.weekSelector === 'isoEven');
}

/** 'odd weeks (ISO)' / 'even weeks (ISO)' — only for the parity selectors. */
function isoParityLabel(calendar: ScheduleCalendarRule): string | undefined {

    if (!isIsoParity(calendar)) {
        return undefined;
    }

    return calendar.weekSelector === 'isoOdd' ? 'odd weeks (ISO)' : 'even weeks (ISO)';
}

function weeklyPattern(calendar: ScheduleCalendarRule, fixedInterval?: number): string {

    const interval = fixedInterval ?? calendar.weeklyInterval ?? 1;
    const days = dayList(calendar.daysOfWeek);

    if (interval === 1) {
        return `Every ${days}`;
    }

    if (interval === 2) {
        return `Every second ${days}`;
    }

    return `${days}, every ${interval} weeks`;
}

/** '2nd Thursday' / '1st and 3rd Monday' / 'the last day' — the "on" half of a monthly summary. */
function monthlyOnLabel(calendar: ScheduleCalendarRule): string {

    if (calendar.monthlyOn === 'nthWeekday') {
        const weekdayName = DAY_FULL_NAMES[(calendar.weekday ?? 1) - 1];

        return `${joinWithAnd(sortNths(calendar.nths).map(nthLabel))} ${weekdayName}`;
    }

    return daysOfMonthLabel(calendar.daysOfMonth);
}

/** Ascending, with 'last' pushed to the end — mirrors the BYDAY emission order. */
function sortNths(nths: readonly ScheduleNth[] | undefined): ScheduleNth[] {

    const ordinals = nths && nths.length > 0 ? nths : [1 as ScheduleNth];

    return Array.from(new Set(ordinals)).sort((a, b) => {

        if (a === 'last') {
            return b === 'last' ? 0 : 1;
        }

        return b === 'last' ? -1 : a - b;
    });
}

function monthlyPattern(calendar: ScheduleCalendarRule, interval: number): string {

    const months = monthLabel(calendar.months);

    if (months) {
        return `${capitalise(monthlyOnLabel(calendar))} of ${months}`;
    }

    return `${capitalise(monthlyOnLabel(calendar))} of ${monthCadence(interval)}`;
}

function yearlyPattern(calendar: ScheduleCalendarRule): string {

    const months = monthLabel(calendar.months) ?? MONTH_NAMES[0];

    return `${capitalise(monthlyOnLabel(calendar))} of ${months}`;
}

function capitalise(value: string): string {

    return value.charAt(0).toUpperCase() + value.slice(1);
}

function calendarPattern(calendar: ScheduleCalendarRule): string {

    switch (calendar.repeat) {
        case 'daily':
            return dailyPattern(calendar);
        case 'weekly':
            return weeklyPattern(calendar);
        case 'fortnightly':
            return fortnightlyPattern(calendar);
        case 'monthly':
            return monthlyPattern(calendar, 1);
        case 'quarterly':
            return monthlyPattern(calendar, calendar.monthInterval ?? 3);
        case 'yearly':
            return yearlyPattern(calendar);
    }
}

/** Seasonal months are already folded into monthly/yearly patterns; daily and weekly append them. */
function seasonSuffix(calendar: ScheduleCalendarRule): string | undefined {

    const seasonal = calendar.repeat === 'daily' || calendar.repeat === 'weekly' || calendar.repeat === 'fortnightly';

    return seasonal ? monthLabel(calendar.months) : undefined;
}

function describeCalendar(calendar: ScheduleCalendarRule): string {

    const parts = [calendarPattern(calendar)];
    const parity = isoParityLabel(calendar);

    if (parity) {
        parts.push(parity);
    }

    const season = seasonSuffix(calendar);

    if (season) {
        parts.push(season);
    }

    parts.push(endsLabel(calendar.ends));

    const policy = policyLabel(calendar);

    if (policy) {
        parts.push(policy);
    }

    return parts.join(SEPARATOR);
}

/**
 * '13 Aug 2026' — a listed date reads as a date, not as an ISO stamp.
 *
 * The ends clause keeps its raw YYYY-MM-DD form ('ends 2027-03-31') because there it is a boundary
 * on a pattern, read alongside the pattern that produced it. A dates schedule has no pattern: the
 * dates ARE the summary, and a row of ISO stamps is the part a reader has to decode.
 */
function longDate(date: string): string {

    const [year, month, day] = date.split('-');

    return `${Number(day)} ${MONTH_NAMES[Number(month) - 1]} ${year}`;
}

/** '13 Aug' — the compact form, year dropped the way the chip drops every other qualifier. */
function shortDate(date: string): string {

    const [, month, day] = date.split('-');

    return `${Number(day)} ${MONTH_NAMES[Number(month) - 1]}`;
}

/** '13 Aug 2026 · 24 Sep 2026' — every listed date, in order. */
function describeDates(dates: ScheduleDatesRule): string {

    const listed = normaliseScheduleDates(dates);

    if (listed.length === 0) {
        return 'No dates';
    }

    return listed.map(longDate).join(SEPARATOR);
}

/** How many dates a chip spells out before it counts them instead. */
const SHORT_DATES_LIMIT = 2;

/** '13 Aug · 24 Sep' up to two dates, '5 dates' beyond that — the chip cannot carry a longer list. */
function describeDatesShort(dates: ScheduleDatesRule): string {

    const listed = normaliseScheduleDates(dates);

    if (listed.length === 0) {
        return 'No dates';
    }

    if (listed.length > SHORT_DATES_LIMIT) {
        return `${listed.length} dates`;
    }

    return listed.map(shortDate).join(SEPARATOR);
}

/**
 * The long form, which is where the compliance detail belongs.
 *
 * Both new clauses change what a scheduler is allowed to do, so they must be visible somewhere: the
 * leeway says how early the work may be booked, and 'anniversary' says an early service will NOT pull
 * the regime forward. `describeShort` deliberately keeps neither — it is a chip, and it already drops
 * the ends clause and non-working-day policy for the same reason.
 *
 * Absent leeway and absent (or 'completion') anchor produce byte-identical output to before, so no
 * stored schedule's description changes.
 */
function describeAfterService(afterService: ScheduleAfterServiceRule): string {

    const interval = afterService.interval;
    const unit = interval === 1 ? afterService.unit.slice(0, -1) : afterService.unit;
    const clauses = [`${interval} ${unit} after each service`];

    const leeway = describeLeeway(afterService.earlyLeeway, afterService.lateLeeway);
    if (leeway) clauses.push(leeway);

    if (afterService.anchor === 'anniversary') {
        clauses.push('fixed anniversary');
    }

    return clauses.join(SEPARATOR);
}

/** Equal tolerances collapse to "early or late" so the common symmetric window reads as one phrase. */
function describeLeeway(early: ScheduleLeeway | undefined, late: ScheduleLeeway | undefined): string | undefined {

    const earlyText = early && early.value > 0 ? leewayDuration(early) : undefined;
    const lateText = late && late.value > 0 ? leewayDuration(late) : undefined;
    if (earlyText && lateText) {
        return earlyText === lateText
            ? `serviceable up to ${earlyText} early or late`
            : `serviceable up to ${earlyText} early and ${lateText} late`;
    }
    if (earlyText) return `serviceable up to ${earlyText} early`;
    if (lateText) return `serviceable up to ${lateText} late`;

    return undefined;
}

function leewayDuration(leeway: ScheduleLeeway): string {

    return `${leeway.value} ${leeway.value === 1 ? leeway.unit.slice(0, -1) : leeway.unit}`;
}

function describePerVisit(perVisit: SchedulePerVisitRule): string {

    return perVisit.nth <= 1 ? 'Every visit' : `Every ${ordinal(perVisit.nth)} visit`;
}

export function describeSchedule(editor: ScheduleRule | undefined): string {

    if (isWorkingDaysSchedule(editor)) {
        return WORKING_DAYS_DESCRIPTION;
    }

    if (editor!.kind === 'calendar' && editor!.calendar) {
        return describeCalendar(editor!.calendar);
    }

    if (editor!.kind === 'afterService' && editor!.afterService) {
        return describeAfterService(editor!.afterService);
    }

    if (editor!.kind === 'perVisit' && editor!.perVisit) {
        return describePerVisit(editor!.perVisit);
    }

    if (editor!.kind === 'dates' && editor!.dates) {
        return describeDates(editor!.dates);
    }

    return 'No schedule';
}

// ── Compact forms (describeShort) ────────────────────────────────────
//
// A planner row shows the schedule in a chip beside the task name, where the
// full summary truncates mid-word. describeShort trades precision the row
// cannot show anyway — the ends clause and the non-working-day policy — for a
// form that fits: consecutive weekday runs collapse to a dash range, and each
// repeat family gets a trade shorthand ('fortnightly (A)', '1st Mon qtrly').
// Common shapes land at or under ~24 characters. The full describe() belongs
// in the row's tooltip.

/** Invariant abbreviations — '1 mo' reads as naturally as '6 mo', so the short form does not singularise. */
const SHORT_AFTER_SERVICE_UNITS: Readonly<Record<ScheduleAfterServiceRule['unit'], string>> = { days: 'days', months: 'mo', years: 'yrs' };

/** 'Mon–Thu' for a consecutive run, 'Mon, Wed & Fri' when there is a gap. Mon–Sun never wraps: the week is a list, not a circle. */
function shortDayList(days: readonly ScheduleWeekday[] | undefined): string {

    const sorted = Array.from(new Set(days ?? [1])).sort((a, b) => a - b);
    const names = sorted.map(day => DAY_NAMES[day - 1]);

    if (names.length === 1) {
        return names[0];
    }

    const consecutive = sorted.every((day, index) => index === 0 || day === sorted[index - 1] + 1);

    if (consecutive && names.length >= 3) {
        return `${names[0]}–${names[names.length - 1]}`;
    }

    return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/** '1st', '1st & 15th', 'last' — the compact day-of-month half, article dropped. */
function shortDaysOfMonth(daysOfMonth: readonly (number | 'last')[] | undefined): string {

    const labels = sortMonthDays(daysOfMonth).map(day => (day === 'last' ? 'last' : ordinal(day)));

    if (labels.length === 1) {
        return labels[0];
    }

    return `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
}

/** '1st', '1st & 15th', '1st Mon', 'last Fri' — the "on" half, abbreviated weekday. */
function shortMonthlyOn(calendar: ScheduleCalendarRule): string {

    if (calendar.monthlyOn === 'nthWeekday') {
        const weekdayName = DAY_NAMES[(calendar.weekday ?? 1) - 1];
        const nths = sortNths(calendar.nths).map(nthLabel);

        return `${nths.length === 1 ? nths[0] : `${nths.slice(0, -1).join(', ')} & ${nths[nths.length - 1]}`} ${weekdayName}`;
    }

    return shortDaysOfMonth(calendar.daysOfMonth);
}

/** 'Jan · Apr' — the compact month filter. A run still collapses to a season range. */
function shortMonthLabel(months: readonly number[] | undefined): string | undefined {

    if (!months || months.length === 0) {
        return undefined;
    }

    const unique = Array.from(new Set(months)).sort((a, b) => a - b);

    if (unique.length === 1) {
        return MONTH_NAMES[unique[0] - 1];
    }

    return seasonRange(unique) ?? unique.map(month => MONTH_NAMES[month - 1]).join(' · ');
}

/** 'monthly' / 'qtrly' / 'ev 6 mo' — the compact interval cadence. */
function shortMonthCadence(interval: number): string {

    if (interval === 1) {
        return 'monthly';
    }

    if (interval === 3) {
        return 'qtrly';
    }

    if (interval === 12) {
        return 'yearly';
    }

    return `ev ${interval} mo`;
}

function shortDaily(calendar: ScheduleCalendarRule): string {

    switch (calendar.dailyPreset ?? 'everyDay') {
        case 'weekdays':
            return 'Mon–Fri';
        case 'sixDay':
            return 'Mon–Sat';
        case 'weekend':
            return 'Sat & Sun';
        case 'everyNDays': {
            const interval = calendar.dailyInterval ?? 2;

            return interval === 1 ? 'daily' : `every ${interval} days`;
        }
        default:
            return 'daily';
    }
}

function shortWeekly(calendar: ScheduleCalendarRule): string {

    const interval = calendar.weeklyInterval ?? 1;
    const days = shortDayList(calendar.daysOfWeek);

    if (interval === 1) {
        return `${days} weekly`;
    }

    return `${days} ev ${interval} wks`;
}

/** A/B name the phase because the two produce different dates; ISO parity names the parity. */
function shortFortnightly(calendar: ScheduleCalendarRule): string {

    const days = shortDayList(calendar.daysOfWeek);

    if (isIsoParity(calendar)) {
        return `${days} ${calendar.weekSelector === 'isoOdd' ? 'odd' : 'even'} wks (ISO)`;
    }

    return `${days} fortnightly (${calendar.weekSelector ?? 'A'})`;
}

/**
 * Monthly family. A months filter overrides the interval in compile(), so the
 * short form names the months instead of the cadence — the same rule the
 * editor applies when it hides the interval stepper.
 */
function shortMonthly(calendar: ScheduleCalendarRule, interval: number): string {

    const months = shortMonthLabel(calendar.months);
    const on = shortMonthlyOn(calendar);

    return months ? `${on} ${months}` : `${on} ${shortMonthCadence(interval)}`;
}

function shortYearly(calendar: ScheduleCalendarRule): string {

    return `${shortMonthlyOn(calendar)} ${shortMonthLabel(calendar.months) ?? MONTH_NAMES[0]}`;
}

function shortCalendar(calendar: ScheduleCalendarRule): string {

    switch (calendar.repeat) {
        case 'daily':
            return withShortSeason(shortDaily(calendar), calendar);
        case 'weekly':
            return withShortSeason(shortWeekly(calendar), calendar);
        case 'fortnightly':
            return withShortSeason(shortFortnightly(calendar), calendar);
        case 'monthly':
            return shortMonthly(calendar, 1);
        case 'quarterly':
            return shortMonthly(calendar, calendar.monthInterval ?? 3);
        case 'yearly':
            return shortYearly(calendar);
    }
}

/** Daily/weekly/fortnightly carry the seasonal filter as a trailing segment; the monthly family folds it in. */
function withShortSeason(pattern: string, calendar: ScheduleCalendarRule): string {

    const season = shortMonthLabel(calendar.months);

    return season ? `${pattern}${SEPARATOR}${season}` : pattern;
}

/**
 * Compact schedule summary for planner rows and chips — no ends clause, no
 * non-working-day policy. Pair it with describeSchedule() as the tooltip.
 *
 * Deterministic: same editor model in ⇒ identical string out.
 */
export function describeScheduleShort(editor: ScheduleRule | undefined): string {

    if (isWorkingDaysSchedule(editor)) {
        return WORKING_DAYS_SHORT_DESCRIPTION;
    }

    if (editor!.kind === 'calendar' && editor!.calendar) {
        return shortCalendar(editor!.calendar);
    }

    if (editor!.kind === 'afterService' && editor!.afterService) {
        const { interval, unit } = editor!.afterService;

        return `${interval} ${SHORT_AFTER_SERVICE_UNITS[unit]} after service`;
    }

    if (editor!.kind === 'perVisit' && editor!.perVisit) {
        return editor!.perVisit.nth <= 1 ? 'every visit' : `every ${ordinal(editor!.perVisit.nth)} visit`;
    }

    if (editor!.kind === 'dates' && editor!.dates) {
        return describeDatesShort(editor!.dates);
    }

    return 'No schedule';
}
